import React, { useState } from 'react';
import { Settings, Mail, Lock, User, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const AdminSettingsAccountTab = ({ user, onEmailUpdate }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [emailForm, setEmailForm] = useState({ new_email: user?.email || '', current_password: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [nameForm, setNameForm] = useState({ new_name: user?.name || '' });

  const [emailLoading, setEmailLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);

  const [emailMsg, setEmailMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [nameMsg, setNameMsg] = useState(null);

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    setEmailLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings/email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(emailForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update email');
      setEmailMsg({ type: 'success', text: 'Email updated successfully' });
      setEmailForm(prev => ({ ...prev, current_password: '' }));
      if (onEmailUpdate) onEmailUpdate(data.email);
    } catch (err) {
      setEmailMsg({ type: 'error', text: err.message });
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMsg({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (passwordForm.new_password.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update password');
      setPasswordMsg({ type: 'success', text: 'Password updated successfully' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.message });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleNameUpdate = async (e) => {
    e.preventDefault();
    setNameMsg(null);
    if (!nameForm.new_name.trim()) {
      setNameMsg({ type: 'error', text: 'Name is required' });
      return;
    }
    setNameLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings/name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(nameForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update name');
      setNameMsg({ type: 'success', text: 'Name updated successfully' });
    } catch (err) {
      setNameMsg({ type: 'error', text: err.message });
    } finally {
      setNameLoading(false);
    }
  };

  const StatusMessage = ({ msg }) => {
    if (!msg) return null;
    return (
      <div className={`flex items-center gap-2 text-sm mt-3 p-3 rounded-lg ${
        msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
      }`} data-testid="settings-status-message">
        {msg.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
        {msg.text}
      </div>
    );
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-settings-tab">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-[#BA1826]" />
        <h2 className="font-heading text-xl font-bold text-[#0A0A0A]">Account Settings</h2>
      </div>

      {/* Display Name */}
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-[#4B5563]" />
          <h3 className="font-heading font-semibold text-[#0A0A0A]">Display Name</h3>
        </div>
        <form onSubmit={handleNameUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Name</label>
            <input
              type="text"
              value={nameForm.new_name}
              onChange={(e) => setNameForm({ new_name: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
              data-testid="settings-name-input"
            />
          </div>
          <button
            type="submit"
            disabled={nameLoading}
            className="btn-primary"
            data-testid="settings-save-name-btn"
          >
            {nameLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Update Name
          </button>
          <StatusMessage msg={nameMsg} />
        </form>
      </div>

      {/* Email */}
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-[#4B5563]" />
          <h3 className="font-heading font-semibold text-[#0A0A0A]">Change Email</h3>
        </div>
        <form onSubmit={handleEmailUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">New Email Address</label>
            <input
              type="email"
              value={emailForm.new_email}
              onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
              required
              data-testid="settings-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Current Password</label>
            <input
              type="password"
              value={emailForm.current_password}
              onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
              required
              placeholder="Enter current password to confirm"
              data-testid="settings-email-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={emailLoading}
            className="btn-primary"
            data-testid="settings-save-email-btn"
          >
            {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Update Email
          </button>
          <StatusMessage msg={emailMsg} />
        </form>
      </div>

      {/* Password */}
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-[#4B5563]" />
          <h3 className="font-heading font-semibold text-[#0A0A0A]">Change Password</h3>
        </div>
        <form onSubmit={handlePasswordUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Current Password</label>
            <input
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
              required
              data-testid="settings-current-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">New Password</label>
            <input
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
              required
              minLength={6}
              placeholder="Minimum 6 characters"
              data-testid="settings-new-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Confirm New Password</label>
            <input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
              required
              minLength={6}
              data-testid="settings-confirm-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={passwordLoading}
            className="btn-primary"
            data-testid="settings-save-password-btn"
          >
            {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Update Password
          </button>
          <StatusMessage msg={passwordMsg} />
        </form>
      </div>
    </div>
  );
};

export default AdminSettingsAccountTab;
